<template>
  <div class="employee-payment-detail">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <ProgressSpinner />
      <p>{{ t('Loading payment details...') }}</p>
    </div>

    <!-- Payment Details -->
    <div v-else-if="execution" class="payment-container">
      <!-- Header -->
      <div class="payment-header">
        <Button
          data-testid="employee-payment-back-btn"
          icon="pi pi-arrow-left"
          class="p-button-text"
          @click="goBack"
          :label="t('Voltar')"
        />
        <div class="header-title">
          <h1>{{ t('Detalhes do pagamento') }}</h1>
          <Tag :severity="getStatusSeverity(execution.status)">
            {{ t(capitalizeFirst(execution.status)) }}
          </Tag>
        </div>
      </div>

      <!-- Payment Info Card -->
      <box class="payment-info p-mb-3" :title="t('Informações do pagamento')" icon="pi pi-info-circle">
        <div class="info-grid">
          <div class="info-item">
            <span class="label">{{ t('Período') }}</span>
            <span class="value">{{ execution.period_name }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ t('Regra de pagamento') }}</span>
            <span class="value">{{ execution.rule_description }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ t('Frequência') }}</span>
            <span class="value">{{ getFrequencyLabel() || execution.frequency }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ t('Tipo de regra') }}</span>
            <span class="value">{{ getRuleTypeLabel(execution.rule_type) }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ t('Datas do período') }}</span>
            <span class="value">{{ execution.period_start_date }} - {{ execution.period_end_date }}</span>
          </div>
          <div class="info-item" v-if="execution.payment_date">
            <span class="label">{{ t('Data de pagamento') }}</span>
            <span class="value">{{ execution.payment_date }}</span>
          </div>
        </div>
      </box>

      <!-- Amount Breakdown -->
      <box class="amount-breakdown p-mb-3" :title="t('Detalhamento do valor')" icon="pi pi-dollar">
        <div class="breakdown-container">
          <div class="breakdown-row">
            <span class="breakdown-label">{{ t('Valor bruto') }}</span>
            <span class="breakdown-value gross">{{ n(toNumber(execution.amount), 'currency') }}</span>
          </div>

          <div v-if="execution.deductions && execution.deductions.length > 0" class="deductions-section">
            <Divider align="left">
              <span class="deductions-title">
                <i class="pi pi-minus-circle"></i>
                {{ t('Deduções') }}
              </span>
            </Divider>

            <div
              v-for="deduction in execution.deductions"
              :key="deduction.id"
              class="deduction-row"
            >
              <div class="deduction-info">
                <span class="deduction-reason">{{ deduction.reason }}</span>
                <small class="deduction-date">{{ deduction.created_at }}</small>
              </div>
              <span class="deduction-amount">-{{ n(toNumber(deduction.amount), 'currency') }}</span>
            </div>

            <div class="breakdown-row subtotal">
              <span class="breakdown-label">{{ t('Total de deduções') }}</span>
              <span class="breakdown-value">{{ n(totalDeductions, 'currency') }}</span>
            </div>
          </div>

          <Divider />

          <div class="breakdown-row total">
            <span class="breakdown-label">{{ t('Valor líquido') }}</span>
            <span class="breakdown-value">{{ n(netAmount, 'currency') }}</span>
          </div>
        </div>
      </box>

      <!-- Receipt Section -->
      <box class="receipt-section" v-if="execution.status === 'paid'" :title="t('Payment Receipt')" icon="pi pi-file-pdf">
        <div v-if="execution.receipt_url" class="receipt-content">
          <div class="receipt-info">
            <i class="pi pi-check-circle" style="color: var(--green-500); font-size: 2rem;"></i>
            <div class="receipt-details">
              <span class="receipt-label">{{ t('Comprovante disponível') }}</span>
              <small>{{ t('Baixar ou visualizar seu comprovante de pagamento') }}</small>
            </div>
          </div>
          <Button
            data-testid="employee-payment-view-receipt-btn"
            :label="t('Ver comprovante')"
            icon="pi pi-external-link"
            @click="viewReceipt"
          />
        </div>
        <div v-else class="no-receipt">
          <i class="pi pi-info-circle"></i>
          <p>{{ t('Comprovante ainda não enviado') }}</p>
          <small>{{ t('O comprovante de pagamento estará disponível em breve') }}</small>
        </div>
      </box>

      <!-- Calculation Details (for commission rules) -->
      <box
        v-if="execution.calculation_details"
        class="calculation-details p-mb-3"
        :title="t('Detalhes do cálculo')"
        icon="pi pi-calculator"
      >
        <div class="calculation-info">
          <div class="calc-row" v-if="execution.calculation_details.gross_revenue">
            <span class="calc-label">{{ t('Receita bruta') }}</span>
            <span class="calc-value">{{ n(toNumber(execution.calculation_details.gross_revenue), 'currency') }}</span>
          </div>
          <div class="calc-row" v-if="execution.calculation_details.total_expenses">
            <span class="calc-label">{{ t('Total de despesas') }}</span>
            <span class="calc-value">{{ n(toNumber(execution.calculation_details.total_expenses), 'currency') }}</span>
          </div>
          <div class="calc-row" v-if="execution.calculation_details.profit">
            <span class="calc-label">{{ t('Lucro') }}</span>
            <span class="calc-value">{{ n(toNumber(execution.calculation_details.profit), 'currency') }}</span>
          </div>
          <div class="calc-row" v-if="execution.calculation_details.commission_rate">
            <span class="calc-label">{{ t('Taxa de comissão') }}</span>
            <span class="calc-value">{{ (execution.calculation_details.commission_rate * 100).toFixed(2) }}%</span>
          </div>
        </div>
      </box>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useEmployeePayroll } from '@/composables/useEmployeePayroll';
import { usePaymentRules } from '@/composables/usePaymentRules';
import { log } from '@/utils/DebugLogger';

interface Deduction {
  id?: number | string;
  amount?: number | string;
  reason?: string;
  created_at?: string;
}

interface CalculationDetails {
  gross_revenue?: number | string;
  total_expenses?: number | string;
  profit?: number | string;
  commission_rate?: number;
}

interface PaymentExecution {
  id?: number | string;
  period_name?: string;
  rule_description?: string;
  frequency?: string;
  rule_type?: string;
  period_start_date?: string;
  period_end_date?: string;
  payment_date?: string;
  amount?: number | string;
  deductions?: Deduction[];
  status?: string;
  receipt_url?: string;
  calculation_details?: CalculationDetails;
}

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = parseFloat(String(value ?? '0'));
  return Number.isFinite(parsed) ? parsed : 0;
};

const { t, n } = useI18n();
const router = useRouter();
const route = useRoute();
const { getExecution } = useEmployeePayroll();
const { getRuleTypeLabel, getFrequencyLabel } = usePaymentRules();

const executionId = computed<number>(() => parseInt(String(route.params.id)));
const execution = ref<PaymentExecution | null>(null);
const isLoading = ref<boolean>(false);

const totalDeductions = computed<number>(() => {
  if (!execution.value || !execution.value.deductions) return 0;
  return execution.value.deductions.reduce((sum: number, d) => sum + toNumber(d.amount), 0);
});

const netAmount = computed<number>(() => {
  if (!execution.value) return 0;
  return toNumber(execution.value.amount) - totalDeductions.value;
});

onMounted(async () => {
  await loadExecution();
});

const loadExecution = async (): Promise<void> => {
  log('EmployeePaymentDetail', 'loadExecution', 'Loading execution', { executionId: executionId.value });

  isLoading.value = true;

  try {
    execution.value = await getExecution(executionId.value);

    log('EmployeePaymentDetail', 'loadExecution', 'Execution loaded', execution.value);
  } catch (error) {
    console.error('Error loading execution:', error);
  } finally {
    isLoading.value = false;
  }
};

const goBack = (): void => {
  router.push({ name: 'EmployeePaymentsPage' });
};

const viewReceipt = (): void => {
  if (execution.value?.receipt_url) {
    window.open(execution.value.receipt_url, '_blank');
  }
};

const capitalizeFirst = (str: string | undefined | null): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getStatusSeverity = (status: string | undefined): string => {
  const severities: Record<string, string> = {
    calculated: 'info',
    paid: 'success',
    reversed: 'danger'
  };
  return severities[status ?? ''] || 'secondary';
};
</script>

<style scoped>
.employee-payment-detail {
  padding: 2rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.payment-container {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
}

.payment-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-title h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-title i {
  color: var(--primary-color);
}

.payment-info {
  background-color: var(--surface-50);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item .label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
}

.info-item .value {
  font-weight: 600;
  color: var(--text-color);
}

.breakdown-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
}

.breakdown-row.subtotal {
  border-top: 1px solid var(--surface-300);
}

.breakdown-row.total {
  font-size: 1.25rem;
  font-weight: 600;
  padding: 1rem 0;
}

.breakdown-label {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.breakdown-value {
  font-weight: 600;
  color: var(--text-color);
}

.breakdown-value.gross {
  color: var(--primary-color);
  font-size: 1.5rem;
}

.breakdown-row.total .breakdown-value {
  color: var(--green-600);
}

.deductions-section {
  background-color: var(--surface-50);
  border-radius: 6px;
  padding: 1rem;
  margin: 0.5rem 0;
}

.deductions-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--orange-500);
}

.deduction-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background-color: white;
  border-radius: 4px;
  border-left: 3px solid var(--orange-500);
  margin-bottom: 0.5rem;
}

.deduction-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.deduction-reason {
  font-weight: 500;
  color: var(--text-color);
}

.deduction-date {
  color: var(--text-color-secondary);
  font-size: 0.75rem;
}

.deduction-amount {
  font-weight: 600;
  color: var(--orange-500);
}

.receipt-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: var(--green-50);
  border-radius: 6px;
  border: 1px solid var(--green-200);
}

.receipt-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.receipt-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.receipt-label {
  font-weight: 600;
  color: var(--green-700);
}

.no-receipt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 0.5rem;
  text-align: center;
}

.no-receipt i {
  font-size: 2rem;
  color: var(--text-color-secondary);
}

.no-receipt p {
  margin: 0;
  font-weight: 500;
  color: var(--text-color-secondary);
}

.calculation-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.calc-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--surface-200);
}

.calc-row:last-child {
  border-bottom: none;
}

.calc-label {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.calc-value {
  font-weight: 600;
  color: var(--text-color);
}
</style>
